import { Link } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { Reveal } from '@/components/landing/Reveal'
import { HeroComposition } from '@/components/landing/HeroComposition'
import {
  InstallFlowMockup,
  MyModelsMockup,
  RecommenderMockup,
} from '@/components/landing/VisionMockups'
import { PageOutro, StatusChip, VisionSection } from '@/components/landing/Section'

/**
 * The NevTan AI home page.
 *
 * <p>The site is a set of pages rather than one long scroll: this one makes
 * the case, and each capability has a page of its own reachable from the
 * masthead. What has not changed is the honesty rule -- every capability
 * carries its real status wherever it is mentioned, and the roadmap page
 * states plainly which parts exist.
 */
export function LandingPage() {
  return (
    <>
      <VisionHero />
      <Philosophy />
      <MatchedToMachine />
      <Suite />
      <PageOutro>
        <h2 className="nv-h2 text-[1.6rem] sm:text-[2rem]">Get started in a few minutes</h2>
        <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-brand-body">
          Install the app, let it check your computer, and run your first model locally. Free to
          use.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/download" className="nv-btn-primary-lg">
            Download NevTan AI <ArrowRight size={16} />
          </Link>
          <Link to="/features" className="nv-btn-outline-lg">See what it does</Link>
        </div>
      </PageOutro>
    </>
  )
}

function VisionHero() {
  return (
    <section id="top" className="relative scroll-mt-16 overflow-hidden border-b border-brand-line">
      {/* The suite's hero wash: two soft radials, teal at the top left and a
          paler mint at the top right, over the page ground. In dark the same
          two lights burn through a much deeper ground, which is what gives an
          AI product its atmosphere without abandoning the suite palette. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(1100px_at_15%_-10%,#ccfbf1,transparent_55%),radial-gradient(900px_at_95%_0%,#ecfdf5,transparent_50%)] dark:bg-[radial-gradient(1100px_at_15%_-10%,rgba(45,212,191,.18),transparent_55%),radial-gradient(900px_at_95%_0%,rgba(15,118,110,.22),transparent_50%)]"
      />
      <div className="relative nv-container pb-16 pt-14 sm:pb-20 sm:pt-20">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-lg border border-brand-tint bg-brand-paper px-2.5 py-1 text-[13px] font-bold text-brand-deep">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            The local AI platform
          </span>
          <h1 className="nv-h1 mt-5 text-[2.35rem] leading-[1.08] sm:text-[3.25rem]">
            Run AI models on
            <br />
            <span className="text-brand">your own computer.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-[17px] leading-relaxed text-brand-body">
            NevTan AI checks what your hardware can handle, installs the model for you, and answers
            locally. No subscription, and nothing charged per message.
          </p>

          <RunsLocallyOn />

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/download" className="nv-btn-primary-lg">
              Download the app <ArrowRight size={16} />
            </Link>
            <Link to="/features" className="nv-btn-outline-lg">
              See what it can do
            </Link>
          </div>

          <p className="mt-5 text-[12.5px] text-brand-muted">
            Free to use. Available today on macOS. Windows and Linux in progress.
          </p>
        </div>

        <Reveal className="mt-14">
          <HeroComposition />
        </Reveal>

        {/* Stated in the hero rather than buried, because the site describes a
            platform that is only partly built. */}
        <div className="mx-auto mt-7 max-w-2xl text-center">
          <p className="text-[12.5px] text-brand-muted">
            This site covers the whole platform. Every feature shows where it stands:
          </p>
          <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2">
            <StatusChip status="today" />
            <StatusChip status="building" />
            <StatusChip status="planned" />
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * The model ids the app runs, under the hero.
 *
 * <p>This replaced a prompt box that sat here. A text field on a page that
 * looks like every other AI product's promises an answer, and this site cannot
 * give one -- the models run in the installed app, so all a question here could
 * do was carry the text to the download or sign-in page. Typing into something
 * that answers nothing is worse than not offering it.
 *
 * <p>The ids are set in mono because that is what they are -- identifiers you
 * will see again in the app and on Hugging Face, not prose.
 */
function RunsLocallyOn() {
  return (
    <div className="mx-auto mt-7 flex max-w-2xl flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-[11.5px] text-brand-muted">
      <span>Runs locally on</span>
      {['qwen2.5-7b', 'qwen-coder-7b', 'deepseek-r1-8b'].map((id) => (
        <code
          key={id}
          className="rounded-md border border-brand-line bg-brand-panel px-1.5 py-0.5 font-mono text-[11px] text-brand-body"
        >
          {id}
        </code>
      ))}
      <span>and more</span>
    </div>
  )
}

function Philosophy() {
  return (
    <VisionSection
      eyebrow="How it works"
      title="The technical setup, handled for you"
      lede="Running a model locally normally means choosing file formats, working out memory limits, and starting a server from the command line. NevTan AI does all of that for you."
      tone="panel"
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-xl border border-brand-line bg-brand-paper p-5 shadow-card">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-muted">
              What NevTan AI does
            </p>
            <ul className="mt-3 space-y-2">
              {[
                'Picks a version of the model that fits your memory',
                'Checks you have room for it before downloading',
                'Starts and manages the engine that runs it',
                'Keeps track of model files, storage, and updates',
              ].map((line) => (
                <li key={line} className="flex gap-2.5 text-[13.5px] leading-relaxed text-brand-body">
                  <Check size={14} className="mt-[3px] shrink-0 text-brand" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={90}>
          <div className="h-full rounded-xl border-2 border-brand/30 bg-brand-paper p-5 shadow-raised">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-dark">
              What you do
            </p>
            <ol className="mt-3 space-y-2.5">
              {[
                'Open the app and see what your computer can run',
                'Pick a model for the task in front of you',
                'Install it in one click',
                'Start working',
              ].map((line, index) => (
                <li key={line} className="flex gap-3 text-[13.5px] leading-relaxed text-brand-body">
                  <span className="mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-[10.5px] font-bold text-white">
                    {index + 1}
                  </span>
                  {line}
                </li>
              ))}
            </ol>
          </div>
        </Reveal>
      </div>
    </VisionSection>
  )
}


function MatchedToMachine() {
  return (
    <VisionSection
      eyebrow="Hardware aware"
      title="Know what your computer can run"
      lede="NevTan AI reads your processor, memory, and free disk space, then tells you which models will run well — before you download anything."
      status="today"
    >
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start [&>*]:min-w-0">
        <Reveal>
          <RecommenderMockup />
          {/* The readout lists a GPU because detection genuinely reads one. It
              must not be mistaken for the engine using it, which is not shipped. */}
          <p className="mt-3 text-[12px] leading-relaxed text-brand-muted">
            NevTan AI detects your graphics card today, but does not use it to speed up models yet.
            That is in development. For now, model recommendations are based on system memory.
          </p>
        </Reveal>
        <Reveal delay={90}>
          <InstallFlowMockup />
          <div className="mt-3">
            <MyModelsMockup />
          </div>
        </Reveal>
      </div>
    </VisionSection>
  )
}


/**
 * The teal band every NevTan product carries, placing itself inside the suite.
 *
 * <p>The other products are linked at their real addresses; NevTan AI names
 * itself rather than linking, because the reader is already on it.
 */
const SUITE = [
  {
    name: 'NevTan AI',
    href: undefined,
    body: 'Find, install and run open AI models on hardware you already own.',
  },
  {
    name: 'NevTan Mail',
    href: 'https://mail.nevtan.com/',
    body: 'Secure business email, calendar & meetings on your own domain.',
  },
  {
    name: 'NevTan Drive',
    href: 'https://drive.nevtan.com/',
    body: 'Keep, organize & share your files — with storage shared with Mail.',
  },
  {
    name: 'NevTan Cloud',
    href: 'https://cloud.nevtan.com/',
    body: "Store, sync & share your team's files securely in the cloud.",
  },
  {
    name: 'NevTan Sign',
    href: 'https://sign.nevtan.com/',
    body: 'Send, sign & manage documents with legally binding e-signatures.',
  },
  {
    name: 'NevTan Engage',
    href: 'https://engage.nevtan.com/',
    body: 'Reach & engage your customers across channels from one place.',
  },
]

function Suite() {
  return (
    <section className="relative overflow-hidden bg-[linear-gradient(135deg,#0f766e_0%,#115e59_55%,#134e4a_100%)]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_at_85%_20%,rgba(45,212,191,.28),transparent_60%)]"
      />
      <div className="relative nv-container pb-16 pt-12 sm:pb-20 sm:pt-14">
        <span className="nv-eyebrow-invert">One account, one suite</span>
        <h2 className="mt-3 max-w-3xl text-[1.8rem] font-extrabold tracking-display text-white sm:text-[2.2rem]">
          NevTan AI is part of the NevTan suite
        </h2>
        <p className="mt-3 max-w-2xl text-[15.5px] leading-relaxed text-white/75">
          Sign in once and use every NevTan product: local AI, email, files, e-signatures, cloud
          storage, and customer engagement. Built to work together.
        </p>

        <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SUITE.map((product, index) => {
            const inner = (
              <>
                <div className="flex items-center gap-2">
                  <h3 className="text-[15px] font-bold text-white">{product.name}</h3>
                  {!product.href && (
                    <span className="rounded-lg bg-white/20 px-1.5 py-0.5 text-[10.5px] font-bold text-white">
                      You&rsquo;re here
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">{product.body}</p>
              </>
            )

            return (
              <Reveal key={product.name} delay={(index % 3) * 70}>
                {product.href ? (
                  <a
                    href={product.href}
                    target="_blank"
                    rel="noreferrer"
                    className="block h-full rounded-xl border border-white/15 bg-white/[0.08] p-5 transition hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.12]"
                  >
                    {inner}
                  </a>
                ) : (
                  <div className="h-full rounded-xl border border-brand-bright/50 bg-white/[0.14] p-5">
                    {inner}
                  </div>
                )}
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}

